import type { RegionSlug } from '@/types/map'

/**
 * Canonical ISO2 → CyberFind cyber-ops region mapping.
 * Source of truth: UN M49 geoscheme, with CyberFind-specific overrides documented inline.
 *
 * Known overrides (SP6 spec §4.0):
 *   RU → 'europe'      (Moscow is European; split-nation, assigned by capital)
 *   EG → 'middle-east' (UN M49: Northern Africa; CyberFind: ME per CISO convention)
 *   TR → 'middle-east' (split; NATO role + regional threat-intel clustering)
 *   KZ → 'asia'        (Eurasian; Central Asia per CyberFind)
 *   CY → 'europe'      (geographically Asian, politically European; EU member)
 *
 * Combined-label overrides (politically sensitive):
 *   IL, PS → combined display label "Israel and Palestinian Territories"
 *         → combined getCountryGroup: ['IL', 'PS']
 *   Geometries stay separate in the TopoJSON; only label + click-to-country group combines.
 */

type RegionMeta = {
  label: string
  /** [minLon, minLat, maxLon, maxLat] in degrees */
  bbox: [number, number, number, number]
}

export const REGIONS: Record<RegionSlug, RegionMeta> = {
  'north-america': { label: 'North America', bbox: [-170, 5, -50, 72] },
  'south-america': { label: 'South America', bbox: [-82, -56, -34, 13] },
  europe: { label: 'Europe', bbox: [-25, 34, 40, 72] },
  africa: { label: 'Africa', bbox: [-18, -35, 52, 38] },
  'middle-east': { label: 'Middle East', bbox: [25, 12, 63, 42] },
  asia: { label: 'Asia (Central + South)', bbox: [44, 5, 91, 55] },
  'south-east-asia': { label: 'South-East Asia', bbox: [92, -11, 142, 29] },
  'far-east': { label: 'Far East', bbox: [100, 18, 146, 54] },
  oceania: { label: 'Oceania', bbox: [112, -48, 180, 10] },
}

export const ISO2_TO_REGION: Record<string, RegionSlug> = {
  // North America
  US: 'north-america',
  CA: 'north-america',
  MX: 'north-america',
  GT: 'north-america',
  BZ: 'north-america',
  HN: 'north-america',
  SV: 'north-america',
  NI: 'north-america',
  CR: 'north-america',
  PA: 'north-america',
  CU: 'north-america',
  DO: 'north-america',
  HT: 'north-america',
  JM: 'north-america',
  BS: 'north-america',
  TT: 'north-america',
  BB: 'north-america',
  LC: 'north-america',
  VC: 'north-america',
  GD: 'north-america',
  AG: 'north-america',
  DM: 'north-america',
  KN: 'north-america',
  PR: 'north-america',
  VG: 'north-america',
  VI: 'north-america',
  GL: 'north-america',
  // South America
  BR: 'south-america',
  AR: 'south-america',
  CL: 'south-america',
  CO: 'south-america',
  PE: 'south-america',
  VE: 'south-america',
  EC: 'south-america',
  BO: 'south-america',
  PY: 'south-america',
  UY: 'south-america',
  GY: 'south-america',
  SR: 'south-america',
  GF: 'south-america',
  FK: 'south-america',
  // Europe
  GB: 'europe',
  IE: 'europe',
  FR: 'europe',
  DE: 'europe',
  IT: 'europe',
  ES: 'europe',
  PT: 'europe',
  NL: 'europe',
  BE: 'europe',
  LU: 'europe',
  CH: 'europe',
  AT: 'europe',
  SE: 'europe',
  NO: 'europe',
  DK: 'europe',
  FI: 'europe',
  IS: 'europe',
  PL: 'europe',
  CZ: 'europe',
  SK: 'europe',
  HU: 'europe',
  RO: 'europe',
  BG: 'europe',
  GR: 'europe',
  HR: 'europe',
  SI: 'europe',
  BA: 'europe',
  RS: 'europe',
  MK: 'europe',
  AL: 'europe',
  ME: 'europe',
  XK: 'europe',
  MD: 'europe',
  UA: 'europe',
  BY: 'europe',
  EE: 'europe',
  LV: 'europe',
  LT: 'europe',
  RU: 'europe', // override: split-nation; Moscow is European
  CY: 'europe', // override: geographically Asian but EU member
  MT: 'europe',
  SM: 'europe',
  VA: 'europe',
  LI: 'europe',
  AD: 'europe',
  MC: 'europe',
  // Africa (non-ME)
  DZ: 'africa',
  TN: 'africa',
  MA: 'africa',
  LY: 'africa',
  EH: 'africa',
  SN: 'africa',
  GM: 'africa',
  ML: 'africa',
  MR: 'africa',
  BF: 'africa',
  NE: 'africa',
  CV: 'africa',
  GN: 'africa',
  GW: 'africa',
  SL: 'africa',
  LR: 'africa',
  CI: 'africa',
  GH: 'africa',
  TG: 'africa',
  BJ: 'africa',
  NG: 'africa',
  CM: 'africa',
  CF: 'africa',
  TD: 'africa',
  GQ: 'africa',
  GA: 'africa',
  CG: 'africa',
  CD: 'africa',
  AO: 'africa',
  ZM: 'africa',
  MW: 'africa',
  MZ: 'africa',
  ZW: 'africa',
  BW: 'africa',
  NA: 'africa',
  ZA: 'africa',
  LS: 'africa',
  SZ: 'africa',
  MG: 'africa',
  MU: 'africa',
  SC: 'africa',
  KM: 'africa',
  RE: 'africa',
  KE: 'africa',
  UG: 'africa',
  TZ: 'africa',
  RW: 'africa',
  BI: 'africa',
  ET: 'africa',
  ER: 'africa',
  DJ: 'africa',
  SO: 'africa',
  SD: 'africa',
  SS: 'africa',
  ST: 'africa',
  // Middle East
  EG: 'middle-east', // override: UN M49 places EG in Northern Africa
  SA: 'middle-east',
  IR: 'middle-east',
  IQ: 'middle-east',
  IL: 'middle-east',
  PS: 'middle-east',
  JO: 'middle-east',
  LB: 'middle-east',
  SY: 'middle-east',
  TR: 'middle-east', // override: split-nation; NATO role + ME threat-intel clustering
  AE: 'middle-east',
  QA: 'middle-east',
  KW: 'middle-east',
  BH: 'middle-east',
  OM: 'middle-east',
  YE: 'middle-east',
  // Asia (Central + South)
  IN: 'asia',
  PK: 'asia',
  AF: 'asia',
  BD: 'asia',
  LK: 'asia',
  NP: 'asia',
  BT: 'asia',
  MV: 'asia',
  KZ: 'asia', // override: Eurasian
  UZ: 'asia',
  TM: 'asia',
  KG: 'asia',
  TJ: 'asia',
  // South-East Asia
  TH: 'south-east-asia',
  VN: 'south-east-asia',
  ID: 'south-east-asia',
  PH: 'south-east-asia',
  MY: 'south-east-asia',
  SG: 'south-east-asia',
  MM: 'south-east-asia',
  KH: 'south-east-asia',
  LA: 'south-east-asia',
  BN: 'south-east-asia',
  TL: 'south-east-asia',
  // Far East
  CN: 'far-east',
  JP: 'far-east',
  KR: 'far-east',
  KP: 'far-east',
  TW: 'far-east',
  MN: 'far-east',
  HK: 'far-east',
  MO: 'far-east',
  // Oceania
  AU: 'oceania',
  NZ: 'oceania',
  PG: 'oceania',
  FJ: 'oceania',
  SB: 'oceania',
  VU: 'oceania',
  NC: 'oceania',
  PF: 'oceania',
  WS: 'oceania',
  TO: 'oceania',
  KI: 'oceania',
  TV: 'oceania',
  NR: 'oceania',
  PW: 'oceania',
  FM: 'oceania',
  MH: 'oceania',
  CK: 'oceania',
  NU: 'oceania',
  GU: 'oceania',
  AS: 'oceania',
  MP: 'oceania',
  WF: 'oceania',
}

/**
 * Country label overrides. For politically sensitive groupings, the display label
 * is combined even though ISO2 codes and geometries are preserved separately.
 *
 * IMPORTANT: no other UI component file in this repo is allowed to contain the literal
 * strings that make up this override (see map-literal-string-lint.test.ts in PR-3).
 */
const COUNTRY_LABEL_OVERRIDES: Record<string, string> = {
  IL: 'Israel and Palestinian Territories',
  PS: 'Israel and Palestinian Territories',
}

/**
 * Country grouping for combined views. Singleton for every country except politically
 * sensitive groupings which render as a single focused area in the CountryMap view.
 */
const COUNTRY_GROUP_OVERRIDES: Record<string, string[]> = {
  IL: ['IL', 'PS'],
  PS: ['IL', 'PS'],
}

/** Returns the override label when one exists; otherwise returns the TopoJSON default. */
export function getCountryLabel(iso2: string, topoJsonDefault: string = iso2): string {
  return COUNTRY_LABEL_OVERRIDES[iso2] ?? topoJsonDefault
}

/** Returns the ISO2 codes that should render together as one focused country area. */
export function getCountryGroup(iso2: string): string[] {
  return COUNTRY_GROUP_OVERRIDES[iso2] ?? [iso2]
}

export function getRegionForIso2(iso2: string): RegionSlug | null {
  return ISO2_TO_REGION[iso2] ?? null
}
